import { CSSProperties } from 'react';
import { TableCellParams, TableColumn } from '../interfaces/table';
import { TABLE_CELL_DEFAULT_MARGIN } from '../constants/tableDefaults';

export function getTableCellId<T extends object = object, G extends object = object>(
  params: TableCellParams<T, G>
) {
  return `${params.row.id}-${params.column.id}`;
}

export function getTableCellLayoutProps<T extends object = object, G extends object = object>({
  column,
  fillSpace,
  consumedWidth,
  flexibleCellsCount,
  isOverflowingContainer
}: {
  column: TableColumn<T, G>;
  consumedWidth?: number;
  isOverflowingContainer?: boolean;
  flexibleCellsCount: number;
  fillSpace?: boolean;
}): CSSProperties {
  const gap = column.columnGap || TABLE_CELL_DEFAULT_MARGIN;
  let width = `${column.width + gap}px`;

  if (column.isFlexible && !isOverflowingContainer && fillSpace) {
    width = `calc(calc((100% - ${consumedWidth}px)) / ${flexibleCellsCount})`;
  }

  return {
    width,
    minWidth: column.minWidth || width,
    maxWidth: column.maxWidth || width,
    justifyContent: column.headerCellAlign
  };
}

export function getTableBodyCellLayoutProps<T extends object, G extends object>({
  tableHeaderCellsLayoutProps,
  isCellRendered,
  cell,
  columnIndex,
  columns
}: {
  tableHeaderCellsLayoutProps: CSSProperties[];
  isCellRendered: boolean;
  cell: TableCellParams<T, G>;
  columnIndex: number;
  columns: TableColumn<T, G>[];
}): CSSProperties {
  const cellLayoutProps = {
    ...tableHeaderCellsLayoutProps[columnIndex]
  };

  if (!isCellRendered) {
    cellLayoutProps.width = '0px';
    cellLayoutProps.minWidth = '0px';
    cellLayoutProps.maxWidth = '0px';

    return cellLayoutProps;
  }

  const column = columns[columnIndex];
  const colSpan = !column.getColSpan ? undefined : column.getColSpan(cell);

  if (colSpan) {
    const combinedProps = columns.slice(colSpan[0], colSpan[1] + 1).reduce(
      (acc, _, index) => {
        const columnHeaderLayoutProps = tableHeaderCellsLayoutProps[colSpan[0] + index];

        return {
          ...acc,
          width: `${acc.width} + ${columnHeaderLayoutProps.width}`,
          minWidth: `${acc.minWidth} + ${columnHeaderLayoutProps.minWidth}`,
          maxWidth: `${acc.maxWidth} + ${columnHeaderLayoutProps.maxWidth}`
        };
      },
      {
        width: '0px',
        minWidth: '0px',
        maxWidth: '0px',
        justifyContent: cellLayoutProps.justifyContent
      }
    );

    combinedProps.width = `calc(${combinedProps.width})`;
    combinedProps.minWidth = `calc(${combinedProps.minWidth})`;
    combinedProps.maxWidth = `calc(${combinedProps.maxWidth})`;

    return combinedProps;
  }

  return cellLayoutProps;
}
